/**
 * Générateur — Calculer le volume d'une pyramide, d'un cône.
 * Attendu ID : 178 | Niveau : 4ème | Domaine : Grandeurs & mesures (O1)
 *
 * Doc prof MUET (aucun exercice texte ni image dans l'export) → BANQUE créée
 * en juillet 2026 : pyramide à base carrée ou rectangle, cône (coefficient de
 * π, valeur approchée), « quel calcul ? », V/F, problème. Volumes ENTIERS par
 * construction (base·h ≡ 0 mod 3, tirages de grandeurs.js). Figures en
 * perspective cavalière (svgPyramide / svgCone, partagées avec 1241 et 185).
 *
 * Revue 2026-09-23 (vague 3), décisions du coordinateur appliquées en mode
 * auto. Attendu de fin de 4e (Eduscol, attendus de fin d'année, p. 8) : « Il
 * calcule le volume d'une pyramide, d'un cône. » Exemple de réussite : « Il
 * connaît les formules du volume d'une pyramide et d'un cône et sait les
 * utiliser. » Cours (19Espace2) : l'aire de la base, puis (B × h) ÷ 3 écrit
 * en fraction ; cône de rayon 3 cm et de hauteur 6 cm, « 18π cm³ (valeur
 * exacte) ≈ 56,55 cm³ (valeur approchée) », avec la touche π.
 *   - Pyramide à base carrée (pyramide_carree_saisie) : « régulière » retiré
 *     (absent du cours et de l'attendu, le volume n'en dépend pas) ; alt.
 *   - Pyramide à base rectangle (pyramide_rectangle_saisie) : alt.
 *   - Cône, valeur exacte (cone_coef_pi_saisie) : la formule est RETIRÉE de
 *     l'énoncé (« il connaît les formules » ; 1241 l'a retirée aussi) ; « Donne
 *     la valeur exacte du volume de ce cône. », phrase à trous en KaTeX
 *     $\text{V} = ⬚\pi\ \text{cm}^3$ ; plus de « coefficient de π » ni de
 *     parenthèse.
 *   - Cône, valeur approchée (cone_approx_saisie) : seuls les couples (r ; h)
 *     où l'arrondi à l'unité est le même avec 3,14, 3,1416, la touche π et la
 *     touche π arrondie d'abord au centième (38 couples sur 47 ; l'audit en
 *     comptait 39 sans le double arrondi, qui écarte aussi 60π = 188,4955… lu
 *     188,50) ; « arrondie à l'unité », sans « π ≈ 3,14 » (le cours prend la
 *     touche π) ; calculatrice affichée (contenu.calculatrice).
 *   - Le couple (3 ; 6) du cours (18π) est SACRIFIÉ sur toutes les cartes du
 *     cône (décidé pour la valeur approchée ; étendu, mode auto, À ARBITRER, à
 *     la valeur exacte et au QCM, où il donne le même 18π).
 *   - « Quel calcul ? » (volume_calcul_qcm) : l'écriture du cours en fraction
 *     et une grille aire × tiers : clé \dfrac{a × a × h}{3}, oubli_du_tiers
 *     a × a × h, aire_base_confondue \dfrac{a × h}{3}, les_deux a × h ; a = 3
 *     exclu (a × h y vaut la clé) ; variante cône une fois sur deux, comme 1241,
 *     sur la même grille (rayon_non_au_carre \dfrac{π × r × h}{3}, les_deux
 *     π × r × h ; r = 3 exclu pour la même raison) ; options toutes en KaTeX,
 *     de même allure. Avant, la clé était la seule option la plus longue et
 *     celle qui partageait le plus de morceaux avec les autres (100 %).
 *   - V/F (volume_vf) : le modèle de 1241, quatre familles (volume, aire de la
 *     base, le tiers du pavé, le calcul), une ligne par famille, une à trois
 *     fausses ; la ligne « Le volume d'une pyramide s'exprime en cm³ » (vraie
 *     par défaut) est remplacée par une erreur d'unité, unite_aire, « … est
 *     48 cm² » (fausse), tirée à la place de oubli_du_tiers pour la ligne
 *     fausse du volume ; a ≠ 4 (4a = a²). Avant : deux lignes sur la même
 *     grandeur, une vraie une fausse, dans 100 % des tableaux.
 *   - Problème (probleme_pyramide_saisie) : hauteur au plus égale au côté (une
 *     tente ou un toit n'est jamais deux fois plus haut que large : 26,6 % des
 *     tentes l'étaient) ; « le volume sous ce toit ». Mode auto, À ARBITRER :
 *     « La figure n'est pas à l'échelle. » sur cette carte, le dessin partagé
 *     étant toujours plus haut que large (une figure proportionnelle
 *     demanderait un paramètre de plus à svgPyramide).
 *   - Rappel brouillon IMPÉRATIF sur les sept formats, juste avant la
 *     question (décision : l'attendu se fait à la main).
 *   - alt sur toutes les figures (dimensions de l'énoncé, jamais le volume) ;
 *     le sommet S en KaTeX_Math italique (option lettreMath de svgPyramide et
 *     svgCone, fausse par défaut) ; le reste du dessin n'est pas modifié
 *     (partagé avec 1241 et 185, qui le voient à l'identique).
 *   - Second chemin : chaque volume est recalculé par un autre chemin (aire de
 *     la base puis tiers, en entiers) et chaque option du QCM relue dans son
 *     écriture LaTeX (rationnel × π^k), throw si la clé ne colle pas ou si deux
 *     options coïncident. Aucun solveur test-side ne couvre 178 (l'ancien
 *     en-tête parlait d'un arrondi « partagé gen↔solveur » : il n'existe pas).
 *   - Contrat des distracteurs exporté (REGLES_DISTRACTEURS_QCM), codes dans
 *     _meta.params.distracteurs.
 *
 * ── Révision 2026-09-25 (retour prof, épreuve de Musk) ───────────────────
 * TROIS CARTES au lieu d'une (« sépare la pyramide du cône ; le problème, un
 * bloc à part ») :
 *   « Volume d'une pyramide » : base carrée (saisie), base rectangle (saisie),
 *     « Quel calcul ? » (QCM, pyramide seule), Vrai / Faux.
 *   « Volume d'un cône » : valeur exacte (trou), valeur approchée (saisie,
 *     calculatrice), « Quel calcul ? » (QCM, cône seul). Le QCM mixte
 *     (volume_calcul_qcm, pyramide ou cône une fois sur deux) est COUPÉ en
 *     deux clés, mêmes grilles, mêmes distracteurs.
 *   « Pyramide dans un pavé » (pave_pyramide_saisie, NEUVE, d'après le manuel
 *     du prof : IJDHK dans un cube, ORST dans un pavé) : un pavé droit nommé
 *     (dimensions entières, toutes différentes : trois longueurs à placer),
 *     dessiné à l'échelle (moteur des solides nommés de 185, arêtes cachées en
 *     pointillés), une pyramide en bleu dont le sommet est à l'aplomb d'un
 *     sommet avant de la base : la hauteur est une arête verticale, à l'élève
 *     de le voir ; sa base est le triangle rectangle au coin, bordé par une
 *     arête entière et un demi-côté (un milieu) ou par deux demi-côtés (deux
 *     milieux), une fois sur deux. Les milieux sont DITS dans l'énoncé et
 *     codés sur la figure. « Calcule le volume exact de la pyramide FMBC, en
 *     cm³. » Volume entier par construction (L × l × h multiple de 24 : les
 *     deux variantes tombent juste), recalculé depuis les sommets 3D dessinés.
 *     Regardé en PNG (quatre tirages, les deux variantes, les deux coins) :
 *     arêtes cachées et milieux lisibles ; la plus grande dimension est bornée
 *     à 2,5 fois la plus petite (4 × 9 × 12 faisait une aiguille).
 *     Épreuve de Musk (prof) : PAS de cube (toutes les arêtes égales, plus
 *     rien à lire), pas la demi-face par une diagonale (le GABC du cours, sans
 *     milieu : réservé à la fiche coup de pouce).
 *   L'ancien « Problème » (tente, toit, monument : la saisie simple avec un
 *   décor) est SUPPRIMÉ.
 * Erreurs nommées de la carte neuve, pour la fiche : milieu_ignore (la face
 * entière pour base), triangle_sans_moitie, hauteur_oblique (une arête
 * penchée prise pour la hauteur), oubli_du_tiers.
 *
 * ── Révision 2026-09-25, soir (retour prof, épreuve de Musk vue de l'écran) ─
 * Un onglet ne dit que le FORMAT (Saisie, QCM, Vrai/Faux…), jamais un
 * contenu ; ce qui distinguait deux onglets de même format devient un tirage :
 *   « Volume d'une pyramide » : Saisie (pyramide_saisie, NEUVE : base carrée ou
 *     rectangle une fois sur deux, _meta.params.base ; les clés
 *     pyramide_carree_saisie et pyramide_rectangle_saisie disparaissent, leurs
 *     tirages restent), QCM (pyramide_calcul_qcm), Vrai/Faux (volume_vf).
 *   « Volume d'un cône » : Saisie (cone_saisie, NEUVE : valeur exacte ou valeur
 *     approchée une fois sur deux, _meta.params.valeur ; cone_coef_pi_saisie et
 *     cone_approx_saisie disparaissent). La valeur exacte, qui était une phrase
 *     à trous en KaTeX, devient une SAISIE au suffixe « π cm³ » (même format que
 *     l'approchée sous le même onglet ; l'élève tape toujours « 32 », précédent
 *     gen_173) ; la calculatrice reste sur l'approchée seule. QCM
 *     (cone_calcul_qcm).
 *   « Pyramide dans un pavé » : Saisie (pave_pyramide_saisie), un seul format,
 *     donc pas d'onglet à l'écran.
 * Six formats, dont deux QCM (un par carte de solide).
 *
 * ── Check calculatrice (prof, 2026-09-25 soir : « n'oublie pas élève de 4e ») ─
 * CALCULATRICE (contenu.calculatrice = true) sur les quatre cartes de calcul :
 * pyramide Saisie (14 × 13 × 15 ÷ 3 = 910 à la main, ce n'est pas l'attendu),
 * pyramide Vrai/Faux (la ligne du volume demande le même calcul), cône Saisie
 * dans ses deux variantes (9² × 12 ÷ 3 = 324 ; l'approchée l'avait déjà pour
 * π, sous un même onglet elle apparaissait une fois sur deux), pavé
 * ((12 × 11 ÷ 2) × 10 ÷ 3). Le rappel brouillon y reste : la formule s'écrit
 * avant de taper. PAS de calculatrice ni de rappel brouillon sur les deux
 * « Quel calcul ? » : on y choisit une écriture, rien ne se calcule (même
 * décision que les étapes de 1087 / 1089).
 */

import {
    randInt, randomChoice, shuffle, tex, pickLettresSains,
    makeSaisie, makeQcm, makeVraiFaux,
} from '../../../utils';
import {
    tirerPyramideCarree, tirerPyramideRect, tirerCone, coneApprox, coneArrondisConcordent,
    svgPyramide, svgCone, imgElement,
} from './grandeurs';
import { pyramideDansPave178 } from '../espace/svgEspace';

const ATTENDU_ID = 178;
const L_PYRAMIDE = 'Volume d’une pyramide';
const L_CONE = 'Volume d’un cône';
const L_PAVE = 'Pyramide dans un pavé';
const POOL = 'ABCDEFGHJKLMNPRSTUV'.split('');
const opts = (label, el) => ({
    niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label,
    ...(el ? { elements_additionnels: el } : {}),
});

// Rappel brouillon (règle prof 2026-09-23, par attendu) : IMPÉRATIF sur les
// quatre cartes de calcul (la formule s'écrit avant de taper), pas sur les deux
// « Quel calcul ? » où rien ne se calcule (prof 2026-09-25). Forme maison du
// parc (gen_149, 162).
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Pose tes calculs au brouillon.</p>';
/** La calculatrice de l'écran sur les cartes de calcul (prof 2026-09-25 : un élève de 4e ne fait pas 14 × 13 × 15 ÷ 3 à la main) ; lue par calculatriceAutorisee. */
const calculatrice = (payload) => ({ ...payload, contenu: { ...payload.contenu, calculatrice: true } });

// ═════════ Figures (dessin partagé, alt propre à 178) ═════════

const figCarree = (a, h, u = 'cm') => imgElement(
    svgPyramide({ base: 'carre', labels: { cote: `${a} ${u}`, h: `${h} ${u}` }, lettreMath: true }), 'small',
    `Pyramide à base carrée, en perspective cavalière : le côté de la base mesure ${a} ${u}, la hauteur, en pointillés rouges, ${h} ${u}.`,
);
const figRect = (L, l, h) => imgElement(
    svgPyramide({ base: 'rectangle', labels: { L: `${L} cm`, l: `${l} cm`, h: `${h} cm` }, lettreMath: true }), 'small',
    `Pyramide à base rectangulaire, en perspective cavalière : la base mesure ${L} cm sur ${l} cm, la hauteur, en pointillés rouges, ${h} cm.`,
);
const figCone = (r, h) => imgElement(
    svgCone({ labels: { r: `${r} cm`, h: `${h} cm` }, lettreMath: true }), 'small',
    `Cône de révolution, en perspective cavalière : le rayon de la base, en bleu, mesure ${r} cm, la hauteur, en pointillés rouges, ${h} cm.`,
);

// ═════════ Tirages et second chemin ═════════

/** Second chemin : l'aire de la base, puis le tiers de (aire × hauteur) ; throw si ce n'est pas V. */
const controlerVolume = (aireBase, h, V, contexte) => {
    const triple = aireBase * h;
    if (triple % 3 !== 0 || triple / 3 !== V) throw new Error(`gen_178 : volume ${contexte} (${aireBase} × ${h} ÷ 3 ≠ ${V})`);
    return V;
};

/** Pyramide à base carrée dont le côté n'est pas dans `exclus` (boucle bornée, repli vérifié). */
const tirerCarreeSauf = (exclus, repli) => {
    for (let essai = 0; essai < 60; essai++) {
        const p = tirerPyramideCarree();
        if (!exclus.includes(p.a)) return p;
    }
    return repli;
};

/** (3 ; 6) : le cône du cours, 18π ≈ 56,55 cm³, sacrifié sur toutes les cartes du cône. */
export const SACRIFIE_CONE = { r: 3, h: 6 };
const tirerConeSauf = (accepte, repli) => {
    for (let essai = 0; essai < 100; essai++) {
        const c = tirerCone();
        if (c.r === SACRIFIE_CONE.r && c.h === SACRIFIE_CONE.h) continue;
        if (accepte(c)) return c;
    }
    return repli;
};

// ─── 1. Volume d'une pyramide (saisie) : base carrée ou rectangle, une fois sur deux ──
const genPyramideCarree = () => {
    const { a, h, V } = tirerPyramideCarree();
    controlerVolume(a * a, h, V, `carrée ${a} ${h}`);
    const payload = makeSaisie(String(V),
        `<p>Une pyramide a une base carrée de côté ${a} cm et une hauteur de ${h} cm.</p>`
        + `${RAPPEL_BROUILLON}<p><b>Calcule son volume, en cm³.</b></p>`,
        { ...opts(L_PYRAMIDE, figCarree(a, h)), suffixe: 'cm³' });
    payload._meta.params = { base: 'carree', a, h, V };
    return calculatrice(payload);
};

const genPyramideRect = () => {
    const { L, l, h, V } = tirerPyramideRect();
    controlerVolume(L * l, h, V, `rectangle ${L} ${l} ${h}`);
    const payload = makeSaisie(String(V),
        `<p>Une pyramide a une base rectangulaire de longueur ${L} cm et de largeur ${l} cm. `
        + `Sa hauteur est de ${h} cm.</p>`
        + `${RAPPEL_BROUILLON}<p><b>Calcule son volume, en cm³.</b></p>`,
        { ...opts(L_PYRAMIDE, figRect(L, l, h)), suffixe: 'cm³' });
    payload._meta.params = { base: 'rectangle', L, l, h, V };
    return calculatrice(payload);
};

// Une seule clé (2026-09-25) : la forme de la base est un tirage, pas un onglet.
const genPyramideSaisie = () => (randInt(0, 1) === 0 ? genPyramideCarree() : genPyramideRect());

// ─── 2. Volume d'un cône (saisie) : valeur exacte ou approchée, une fois sur deux ──
const genConeCoef = () => {
    const { r, h, coef } = tirerConeSauf(() => true, { r: 4, h: 6, coef: 32 });
    controlerVolume(r * r, h, coef, `cône ${r} ${h}`);
    // Le champ ne porte que le nombre, π le suit dans le suffixe : l'élève tape « 32 »
    // (saisie depuis le 2026-09-25, même format que la valeur approchée).
    const payload = makeSaisie(String(coef),
        `<p>Un cône a un rayon de ${r} cm et une hauteur de ${h} cm.</p>`
        + `${RAPPEL_BROUILLON}<p><b>Donne la valeur exacte du volume de ce cône.</b></p>`,
        { ...opts(L_CONE, figCone(r, h)), suffixe: 'π cm³' });
    payload._meta.params = { valeur: 'exacte', r, h, coef };
    return calculatrice(payload);
};

const genConeApprox = () => {
    // Repli vérifié : 8π = 25,13… cm³, 25 par les quatre façons d'arrondir.
    const { r, h, coef } = tirerConeSauf((c) => coneArrondisConcordent(c.coef), { r: 2, h: 6, coef: 8 });
    controlerVolume(r * r, h, coef, `cône ${r} ${h}`);
    if (!coneArrondisConcordent(coef)) throw new Error(`gen_178 : arrondis discordants pour ${coef}π`);
    const approx = coneApprox(coef);
    const payload = makeSaisie(String(approx),
        `<p>Un cône a un rayon de ${r} cm et une hauteur de ${h} cm.</p>`
        + `${RAPPEL_BROUILLON}<p><b>Donne une valeur approchée de son volume, arrondie à l’unité.</b></p>`,
        { ...opts(L_CONE, figCone(r, h)), suffixe: 'cm³' });
    // Calculatrice (décision 2026-09-23) : le calcul passe par π, touche ou 3,14,
    // puis par un arrondi.
    payload._meta.params = { valeur: 'approchee', r, h, coef, approx };
    return calculatrice(payload);
};

// Une seule clé (2026-09-25) : exacte ou approchée est un tirage, pas un onglet.
const genConeSaisie = () => (randInt(0, 1) === 0 ? genConeCoef() : genConeApprox());

// ─── 3. Quel calcul donne le volume ? (QCM) ───────────────────
/**
 * Lecture exacte d'une option (second chemin) : \dfrac{…}{…}, \times, \pi et
 * des entiers ; valeur = { p, q } rationnel et k la puissance de π.
 */
const lireCalcul = (latex) => {
    const pgcd = (x, y) => (y ? pgcd(y, x % y) : x);
    const reduire = ({ p, q, k }) => { const g = pgcd(p, q); return { p: p / g, q: q / g, k }; };
    const produit = (s) => s.split('\\times').map((f) => f.trim()).reduce((acc, f) => {
        if (f === '\\pi') return { ...acc, k: acc.k + 1 };
        if (!/^\d+$/.test(f)) throw new Error(`gen_178 : facteur illisible « ${f} » dans ${latex}`);
        return { ...acc, p: acc.p * Number(f) };
    }, { p: 1, q: 1, k: 0 });
    const m = /^\\dfrac\{(.+)\}\{(\d+)\}$/.exec(latex.trim());
    if (m) { const n = produit(m[1]); return reduire({ ...n, q: n.q * Number(m[2]) }); }
    return reduire(produit(latex));
};
const memeValeur = (x, y) => x.p === y.p && x.q === y.q && x.k === y.k;

const assemblerCalcul = (options, texte, figure, params, label) => {
    const valeurs = options.map((o) => lireCalcul(o.latex));
    if (!memeValeur(valeurs[0], params.attendu)) throw new Error(`gen_178 : clé ${options[0].latex}`);
    if (valeurs.some((v, i) => valeurs.some((w, j) => i < j && memeValeur(v, w)))) throw new Error(`gen_178 : deux options égales ${options.map((o) => o.latex)}`);
    const choix = options.map((o) => tex(o.latex));
    const payload = makeQcm(choix, choix[0], texte, opts(label, figure));
    payload._meta.params = { ...params.tirage, distracteurs: options.slice(1).map((o) => o.code) };
    return payload;
};

// Le QCM mixte est coupé en deux depuis le 2026-09-25 (une carte par solide) : mêmes grilles.
const genCalculConeQcm = () => {
    // Cône : r = 3 exclu (π × 3 × h y vaut la clé).
    const { r, h, coef } = tirerConeSauf((c) => c.r !== 3, { r: 4, h: 6, coef: 32 });
    controlerVolume(r * r, h, coef, `cône ${r} ${h}`);
    const options = [
        { latex: `\\dfrac{\\pi \\times ${r} \\times ${r} \\times ${h}}{3}`, code: null },
        { latex: `\\pi \\times ${r} \\times ${r} \\times ${h}`, code: 'oubli_du_tiers' },
        { latex: `\\dfrac{\\pi \\times ${r} \\times ${h}}{3}`, code: 'rayon_non_au_carre' },
        { latex: `\\pi \\times ${r} \\times ${h}`, code: 'les_deux' },
    ];
    return assemblerCalcul(options,
        `<p>Un cône a un rayon de ${r} cm et une hauteur de ${h} cm.</p><p><b>Quel calcul donne son volume, en cm³ ?</b></p>`,
        figCone(r, h), { attendu: { p: coef, q: 1, k: 1 }, tirage: { solide: 'cone', r, h, coef } }, L_CONE);
};

const genCalculPyramideQcm = () => {
    // Pyramide à base carrée : a = 3 exclu (3 × h y vaut 3 × 3 × h ÷ 3).
    const { a, h, V } = tirerCarreeSauf([3], { a: 4, h: 6, V: 32 });
    controlerVolume(a * a, h, V, `carrée ${a} ${h}`);
    const options = [
        { latex: `\\dfrac{${a} \\times ${a} \\times ${h}}{3}`, code: null },
        { latex: `${a} \\times ${a} \\times ${h}`, code: 'oubli_du_tiers' },
        { latex: `\\dfrac{${a} \\times ${h}}{3}`, code: 'aire_base_confondue' },
        { latex: `${a} \\times ${h}`, code: 'les_deux' },
    ];
    return assemblerCalcul(options,
        `<p>Une pyramide a une base carrée de côté ${a} cm et une hauteur de ${h} cm.</p><p><b>Quel calcul donne son volume, en cm³ ?</b></p>`,
        figCarree(a, h), { attendu: { p: V, q: 1, k: 0 }, tirage: { solide: 'pyramide', a, h, V } }, L_PYRAMIDE);
};

// ─── 4. Vrai ou faux (quatre familles, une ligne chacune) ─────
const FAMILLES_VF = ['volume', 'aire', 'tiers', 'calcul'];

const genVolumeVF = () => {
    // a = 4 exclu : 4 × a = a × a, la fausse aire vaudrait la vraie.
    const { a, h, V } = tirerCarreeSauf([4], { a: 6, h: 5, V: 60 });
    controlerVolume(a * a, h, V, `carrée ${a} ${h}`);
    const lignes = {
        volume: {
            vraie: `Le volume de cette pyramide est ${V} cm³.`,
            fausses: [
                { l: `Le volume de cette pyramide est ${a * a * h} cm³.`, code: 'oubli_du_tiers' },
                { l: `Le volume de cette pyramide est ${V} cm².`, code: 'unite_aire' },
            ],
        },
        aire: {
            vraie: `L’aire de sa base est ${a * a} cm².`,
            fausses: [{ l: `L’aire de sa base est ${4 * a} cm².`, code: 'perimetre_pour_aire' }],
        },
        tiers: {
            vraie: 'Son volume est le tiers de celui du pavé de même base et de même hauteur.',
            fausses: [{ l: 'Son volume est le triple de celui du pavé de même base et de même hauteur.', code: 'fois_trois' }],
        },
        calcul: {
            vraie: `Pour trouver son volume, on calcule ${tex(`${a} \\times ${a} \\times ${h}`)}, puis on divise par 3.`,
            fausses: [{ l: `Pour trouver son volume, on calcule ${tex(`${a} \\times ${h}`)}, puis on divise par 3.`, code: 'aire_base_confondue' }],
        },
    };
    if (4 * a === a * a || a * a * h === V) throw new Error(`gen_178 : V/F ${a} ${h}`);
    const nbFausses = randInt(1, 3);
    const fausses = new Set(shuffle(FAMILLES_VF).slice(0, nbFausses));
    const choisies = shuffle(FAMILLES_VF).map((fam) => {
        if (!fausses.has(fam)) return { l: lignes[fam].vraie, v: true, fam };
        const f = randomChoice(lignes[fam].fausses);
        return { l: f.l, v: false, fam, code: f.code };
    });
    const payload = makeVraiFaux(choisies.map((x) => x.l), choisies.map((x) => (x.v ? 0 : 1)),
        `<p>Une pyramide a une base carrée de côté ${a} cm et une hauteur de ${h} cm.</p>`
        + `${RAPPEL_BROUILLON}<p><b>Vrai ou faux ?</b></p>`,
        opts(L_PYRAMIDE, figCarree(a, h)));
    payload._meta.params = { a, h, V, familles: choisies.map((x) => `${x.fam}:${x.v ? 'V' : 'F'}`), distracteurs: choisies.filter((x) => x.code).map((x) => x.code) };
    return calculatrice(payload);
};

// ─── 5. Pyramide dans un pavé (le problème, d'après le manuel du prof) ────

/**
 * Trois dimensions entières de 4 à 12 cm, toutes différentes (trois longueurs à
 * placer), dont le produit est multiple de 24 : le volume est entier dans les
 * deux variantes (un milieu : L·l·h ÷ 12 ; deux milieux : ÷ 24). La plus
 * grande au plus 2,5 fois la plus petite : vu en PNG, un pavé de 4 × 9 × 12
 * fait une aiguille et les lettres de l'arête courte se touchent.
 */
export const tirerPave = () => {
    for (let essai = 0; essai < 300; essai++) {
        const L = randInt(4, 12); const l = randInt(4, 12); const h = randInt(4, 12);
        if (new Set([L, l, h]).size === 3 && (L * l * h) % 24 === 0 && Math.max(L, l, h) <= 2.5 * Math.min(L, l, h)) return { L, l, h };
    }
    return { L: 4, l: 6, h: 9 };
};

/** Aire d'un triangle du sol (y = 0) depuis trois sommets 3D : le déterminant sur (x ; z). */
const aireSol = ([P, Q, R]) => Math.abs((Q.x - P.x) * (R.z - P.z) - (Q.z - P.z) * (R.x - P.x)) / 2;

const genPyramideDansPave = () => {
    const { L, l, h } = tirerPave();
    // Une fois sur deux un seul milieu (sur l'arête avant ou sur l'arête vers l'arrière), une fois sur deux les deux.
    const milieux = randInt(0, 1) === 0 ? [randomChoice(['x', 'z'])] : ['x', 'z'];
    const coin = randInt(0, 1);
    const lettres = pickLettresSains(POOL, 8 + milieux.length);
    const fig = pyramideDansPave178({ L, l, h, lettres, coin, milieux });
    const V = ((milieux.includes('x') ? L / 2 : L) * (milieux.includes('z') ? l / 2 : l) / 2) * h / 3;
    if (!Number.isInteger(V)) throw new Error(`gen_178 : pavé ${L} ${l} ${h} ${milieux} : volume non entier`);
    // Second chemin : l'aire de la base et la hauteur relues sur les sommets 3D DESSINÉS (en cm).
    const base = fig.base.map((nm) => fig.sommets[nm]); const S = fig.sommets[fig.sommet];
    if (base.some((P) => P.y !== 0) || Math.abs((aireSol(base) * S.y) / 3 - V) > 1e-9) throw new Error(`gen_178 : pavé ${fig.nom}, second chemin`);
    const [A, B, , D, E] = lettres;
    const phraseMilieux = fig.milieux.map((m) => `${tex(m.nm)} est le milieu de ${tex(`[${m.seg}]`)}`).join(' et ');
    const texte = `<p>${tex(lettres.slice(0, 8).join(''))} est un pavé droit tel que ${tex(`${A}${B} = ${L}`)} cm, ${tex(`${A}${D} = ${l}`)} cm et ${tex(`${A}${E} = ${h}`)} cm. `
        + `${phraseMilieux.charAt(0).toUpperCase()}${phraseMilieux.slice(1)}.</p>`
        + `${RAPPEL_BROUILLON}<p><b>Calcule le volume exact de la pyramide ${tex(fig.nom)}, en cm³.</b></p>`;
    const payload = makeSaisie(String(V), texte, { ...opts(L_PAVE, imgElement(fig.svg, 'large', fig.alt)), suffixe: 'cm³' });
    payload._meta.params = { L, l, h, milieux: milieux.join(''), coin, V, nom: fig.nom, base: fig.base.join(''), sommet: fig.sommet };
    return calculatrice(payload);
};

// ═════════ Contrat des distracteurs (données pures, jamais lues à l'exécution) ═════════

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'volumes entiers par construction (base·h multiple de 3), recalculés par un second chemin (aire de la base puis tiers), throw sinon',
        'chaque distracteur est une erreur NOMMÉE (codes dans _meta.params.distracteurs), jamais une option inventée',
        'le cône du cours (r = 3 cm, h = 6 cm, 18π) est sacrifié sur toutes les cartes du cône',
    ],
    pyramide_calcul_qcm_et_cone_calcul_qcm: {
        support: 'pyramide à base carrée (a de 4 à 12, a = 3 exclu) sur la carte de la pyramide, cône (r de 2 à 9, r = 3 exclu) sur celle du cône (deux clés depuis le 25/09) ; l\'écriture du cours en fraction, \\dfrac{B × h}{3}',
        grille: 'aire de la base juste ou fausse × tiers ou non : quatre cases de même allure (la clé et oubli_du_tiers ont le même numérateur, la même largeur rendue)',
        familles: {
            oubli_du_tiers: 'a × a × h (π × r × r × h) : le volume du pavé (du cylindre), PRINCIPALE',
            aire_base_confondue: '\\dfrac{a × h}{3} : le côté pris pour l\'aire de la base carrée',
            rayon_non_au_carre: '\\dfrac{π × r × h}{3} : le rayon pris une seule fois (cône)',
            les_deux: 'a × h (π × r × h) : les deux erreurs',
        },
        exclusions: 'a = 3 et r = 3 : les_deux y vaut la clé (3 × h = 3 × 3 × h ÷ 3)',
        supprimees: {
            fois_trois: '× 3 au lieu de ÷ 3 : remplacée par la grille (avec elle, la clé était la seule option la plus longue et celle qui partageait le plus de morceaux avec les autres, 100 %) ; reste au V/F',
            perimetre_pour_aire: '\\dfrac{2 × π × r × h}{3} (1241) : remplacée par les_deux pour tenir la grille ; reste au V/F de la pyramide (4 × a)',
        },
        garantie: 'chaque option relue dans son écriture LaTeX (rationnel × π^k) : la clé vaut le volume, les quatre valeurs sont deux à deux différentes',
    },
    volume_vf: {
        support: 'pyramide à base carrée (a ≠ 4 : 4a = a²) ; quatre familles, une ligne chacune, une à trois fausses',
        familles: {
            oubli_du_tiers: 'volume : a × a × h cm³',
            unite_aire: 'volume : le bon nombre en cm² (remplace la ligne « s\'exprime en cm³ », toujours vraie)',
            perimetre_pour_aire: 'aire de la base : 4 × a cm²',
            fois_trois: 'le triple du pavé au lieu du tiers',
            aire_base_confondue: 'le calcul a × h, puis ÷ 3',
        },
    },
};

// ─── Registry & Export ────────────────────────────────────────

const GENERATORS = {
    pyramide_saisie: genPyramideSaisie,
    pyramide_calcul_qcm: genCalculPyramideQcm,
    volume_vf: genVolumeVF,
    cone_saisie: genConeSaisie,
    cone_calcul_qcm: genCalculConeQcm,
    pave_pyramide_saisie: genPyramideDansPave,
};

// Trois cartes depuis le 2026-09-25 : la pyramide, le cône, le problème ; les
// onglets ne disent que le format (règle prof du soir).
export const CATEGORIES = [
    {
        id: 'pyramide',
        label: L_PYRAMIDE,
        formatsList: [
            { key: 'pyramide_saisie', label: 'Saisie' },
            { key: 'pyramide_calcul_qcm', label: 'QCM' },
            { key: 'volume_vf', label: 'Vrai/Faux' },
        ],
    },
    {
        id: 'cone',
        label: L_CONE,
        formatsList: [
            { key: 'cone_saisie', label: 'Saisie' },
            { key: 'cone_calcul_qcm', label: 'QCM' },
        ],
    },
    {
        id: 'pave',
        label: L_PAVE,
        formatsList: [{ key: 'pave_pyramide_saisie', label: 'Saisie' }],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
